/**
 * Impostazioni di Commesse: **la shelf life**.
 *
 * Enrico (30-08-2026): *«per la shelf life si potrebbe definire in impostazioni,
 * diciamo che come standard sono 18 mesi dalla data di produzione, però potrebbe
 * variare (in bulk o per articolo)»*.
 *
 * Due livelli e nient'altro: il default globale, e l'override per articolo. Il
 * 18 non è nuovo — era già una costante dentro il motore documenti di M3, che da
 * qui in poi legge questa riga. Il punto non è poterlo cambiare spesso (non
 * cambierà): è non averne due, ora che la stessa grandezza serve anche a dire
 * quanta vita resta ai lotti a magazzino.
 *
 * ⚠️ Il **bulk lavora sui codici che hai davanti**, quelli della ricerca fatta
 * qui dentro. Rifare la query lato server sembrerebbe più pulito e sarebbe
 * peggio: fra il momento in cui leggi «184 articoli» e quello in cui confermi,
 * un import può averne cambiato il numero — e toccheresti una lista diversa da
 * quella che hai visto.
 */
import { Check, Info, Loader2, RotateCcw, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useMagazzino,
  useMagazzinoImpostazioni,
  useSalvaImpostazioniMagazzino,
  useShelfLifeArticolo,
  useShelfLifeBulk,
} from "@/hooks/useMagazzino";
import { canUploadDati } from "@/lib/permessi";
import { useMyPermessi } from "@/hooks/usePermessi";
import { num } from "@/lib/numeri";
import { cn } from "@/lib/utils";

const PER_PAGINA = 50;

export function ImpostazioniM1() {
  const { data: perms } = useMyPermessi();
  const puoScrivere = canUploadDati(perms);
  const { data: imp, isLoading } = useMagazzinoImpostazioni();
  const salva = useSalvaImpostazioniMagazzino();

  const [mesi, setMesi] = useState<string>("");
  const [soglia, setSoglia] = useState<string>("");
  useEffect(() => {
    if (!imp) return;
    setMesi(String(imp.shelf_life_mesi_default));
    setSoglia(String(imp.soglia_fine_vita_pct));
  }, [imp]);

  const sporco =
    !!imp &&
    (Number(mesi) !== imp.shelf_life_mesi_default ||
      Number(soglia) !== imp.soglia_fine_vita_pct);

  if (isLoading || !imp) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-6">
      <section className="rounded-lg border bg-card p-4">
        <h3 className="text-sm font-semibold">Shelf life</h3>
        <p className="mt-0.5 max-w-2xl text-[12px] text-muted-foreground">
          Quanto vive un prodotto dalla data di produzione. Non serve a sapere{" "}
          <i>quando</i> scade un lotto — quella data arriva dal file del magazzino — ma a
          leggerla: senza, «scade fra 90 giorni» non dice se è a metà strada o all&apos;ultimo
          mese. Da qui esce anche la data di produzione stampata sui certificati.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-4">
          <Campo
            label="Standard, per tutti gli articoli"
            suffisso="mesi"
            valore={mesi}
            onChange={setMesi}
            disabled={!puoScrivere}
            min={1}
            max={120}
          />
          <Campo
            label="Sotto questa vita residua è «a fine vita»"
            suffisso="%"
            valore={soglia}
            onChange={setSoglia}
            disabled={!puoScrivere}
            min={0}
            max={100}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={!sporco || salva.isPending || !puoScrivere}
              onClick={() =>
                salva.mutate({
                  shelf_life_mesi_default: Number(mesi),
                  soglia_fine_vita_pct: Number(soglia),
                })
              }
            >
              {salva.isPending ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
              Salva
            </Button>
            {salva.isSuccess && !sporco && (
              <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-700 dark:text-emerald-400">
                <Check className="size-3.5" /> salvato
              </span>
            )}
          </div>
        </div>

        <p className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Info className="mt-px size-3.5 shrink-0" />
          <span>
            {imp.n_override > 0 ? (
              <>
                <b className="text-foreground">{imp.n_override} articoli</b> hanno una shelf
                life propria e non seguono questo valore.
              </>
            ) : (
              "Nessun articolo ha una shelf life propria: tutti seguono questo valore."
            )}{" "}
            Cambiarlo muove le date di scadenza calcolate in tutta la piattaforma, documenti
            compresi — per questo serve lo stesso permesso dei caricamenti dati.
          </span>
        </p>
        {!puoScrivere && (
          <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] dark:border-amber-900 dark:bg-amber-950">
            Puoi leggere queste impostazioni ma non cambiarle: serve il permesso di
            caricamento dati.
          </p>
        )}
      </section>

      <PerArticolo puoScrivere={puoScrivere} defaultMesi={imp.shelf_life_mesi_default} />
    </div>
  );
}

function PerArticolo({
  puoScrivere,
  defaultMesi,
}: {
  puoScrivere: boolean;
  defaultMesi: number;
}) {
  const [cerca, setCerca] = useState("");
  const [bozza, setBozza] = useState("");
  const [pagina, setPagina] = useState(0);
  const [valoreBulk, setValoreBulk] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setCerca(bozza), 300);
    return () => clearTimeout(t);
  }, [bozza]);
  useEffect(() => setPagina(0), [cerca]);

  const { data, isLoading } = useMagazzino({
    filtro: "tutti",
    cerca,
    offset: pagina * PER_PAGINA,
    limit: PER_PAGINA,
  });
  const singolo = useShelfLifeArticolo();
  const bulk = useShelfLifeBulk();

  const righe = data?.items ?? [];
  const codiciInPagina = righe.map((r) => r.codice);

  return (
    <section className="rounded-lg border bg-card p-4">
      <h3 className="text-sm font-semibold">Per singolo articolo</h3>
      <p className="mt-0.5 max-w-2xl text-[12px] text-muted-foreground">
        Un valore impostato qui vince sullo standard. Toglierlo (<b>Standard</b>) non è la
        stessa cosa che scriverci sopra il numero dello standard: senza override l&apos;articolo
        si muove quando cambia il valore globale, con l&apos;override resta fermo.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={bozza}
            onChange={(e) => setBozza(e.target.value)}
            placeholder="Cerca codice o descrizione…"
            className="h-9 w-full rounded-md border bg-card pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <span className="text-[11.5px] text-muted-foreground">
          {num(data?.total ?? 0)} articoli
        </span>
      </div>

      {/* Il bulk: agisce sui codici della pagina che hai davanti, e lo dice. */}
      {puoScrivere && righe.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-dashed px-2.5 py-2 text-[11.5px]">
          <span className="text-muted-foreground">
            Applica a tutti i <b className="text-foreground">{righe.length}</b> articoli in
            questa pagina:
          </span>
          <input
            type="number"
            min={1}
            max={120}
            value={valoreBulk}
            onChange={(e) => setValoreBulk(e.target.value)}
            placeholder={String(defaultMesi)}
            className="h-7 w-16 rounded border bg-card px-2 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <span className="text-muted-foreground">mesi</span>
          <Button
            size="sm"
            variant="outline"
            disabled={!valoreBulk || bulk.isPending}
            onClick={() => bulk.mutate({ codici: codiciInPagina, mesi: Number(valoreBulk) })}
          >
            {bulk.isPending ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
            Applica
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={bulk.isPending}
            onClick={() => bulk.mutate({ codici: codiciInPagina, mesi: null })}
            title="Rimette questi articoli a seguire lo standard globale"
          >
            <RotateCcw className="mr-1 size-3.5" />
            Rimetti a standard
          </Button>
          {bulk.isSuccess && (
            <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
              <Check className="size-3.5" /> {bulk.data?.aggiornati} aggiornati
            </span>
          )}
        </div>
      )}

      <div className="mt-2 overflow-hidden rounded-lg border">
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <table className="w-full text-[12px]">
            <thead className="bg-muted/40 text-[10px] text-muted-foreground">
              <tr>
                <th className="px-2.5 py-1.5 text-left font-medium">Articolo</th>
                <th className="px-2.5 py-1.5 text-right font-medium">A magazzino</th>
                <th className="px-2.5 py-1.5 text-left font-medium">Shelf life</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {righe.map((a) => (
                <tr key={a.codice}>
                  <td className="px-2.5 py-1.5">
                    <div className="font-mono text-[11px] font-semibold">{a.codice}</div>
                    <div className="truncate text-[9.5px] text-muted-foreground">
                      {a.descrizione ?? "—"}
                    </div>
                  </td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums text-muted-foreground">
                    {a.pallet > 0 ? `${a.pallet} pallet` : "—"}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <CellaShelfLife
                      codice={a.codice}
                      mesi={a.shelf_life_mesi}
                      impostata={a.shelf_life_impostata}
                      defaultMesi={defaultMesi}
                      disabled={!puoScrivere || singolo.isPending}
                      onSalva={(m) => singolo.mutate({ codice: a.codice, mesi: m })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(data?.total ?? 0) > PER_PAGINA && (
        <div className="mt-2 flex items-center justify-between text-[11.5px]">
          <Button
            size="sm"
            variant="outline"
            disabled={pagina === 0}
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
          >
            Indietro
          </Button>
          <span className="text-muted-foreground">
            {pagina * PER_PAGINA + 1}–{Math.min((pagina + 1) * PER_PAGINA, data?.total ?? 0)} di{" "}
            {num(data?.total ?? 0)}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={(pagina + 1) * PER_PAGINA >= (data?.total ?? 0)}
            onClick={() => setPagina((p) => p + 1)}
          >
            Avanti
          </Button>
        </div>
      )}
    </section>
  );
}

function CellaShelfLife({
  codice,
  mesi,
  impostata,
  defaultMesi,
  disabled,
  onSalva,
}: {
  codice: string;
  mesi: number;
  impostata: boolean;
  defaultMesi: number;
  disabled: boolean;
  onSalva: (mesi: number | null) => void;
}) {
  const [valore, setValore] = useState(String(mesi));
  useEffect(() => setValore(String(mesi)), [mesi, codice]);
  const cambiato = Number(valore) !== mesi;
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        min={1}
        max={120}
        value={valore}
        disabled={disabled}
        onChange={(e) => setValore(e.target.value)}
        onBlur={() => cambiato && Number(valore) > 0 && onSalva(Number(valore))}
        className={cn(
          "h-7 w-16 rounded border bg-card px-2 text-[12px] tabular-nums focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50",
          impostata && "border-brand-600"
        )}
      />
      <span className="text-[10px] text-muted-foreground">mesi</span>
      {impostata ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSalva(null)}
          className="text-[10px] text-brand-700 underline-offset-2 hover:underline disabled:opacity-50 dark:text-brand-400"
          title={`Torna a seguire lo standard (${defaultMesi} mesi)`}
        >
          standard
        </button>
      ) : (
        <span className="text-[10px] text-muted-foreground">dallo standard</span>
      )}
    </div>
  );
}

function Campo({
  label,
  suffisso,
  valore,
  onChange,
  disabled,
  min,
  max,
}: {
  label: string;
  suffisso: string;
  valore: string;
  onChange: (v: string) => void;
  disabled: boolean;
  min: number;
  max: number;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5">
        <input
          type="number"
          min={min}
          max={max}
          value={valore}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-20 rounded-md border bg-card px-2.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
        <span className="text-[12px] text-muted-foreground">{suffisso}</span>
      </span>
    </label>
  );
}
